import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getProjects } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { emptyStateMsg } from '@/utils/styleHelpers';
import { Plus, Search, Filter, Calendar, Users, Target, FolderKanban, Zap } from 'lucide-react';
import { PageTabs } from '@/components/PageTabs';

const HUB_TABS = [
  { path: '/projects', label: 'DMAIC Projects', icon: FolderKanban },
  { path: '/kaizen', label: 'Kaizen Events', icon: Zap },
];
import { industries, phases, getPhaseProgress } from './projectConstants';

export const ProjectsListPage = () => {
  const { isGuest } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');

  useEffect(() => {
    loadProjects();
  }, [industryFilter]);

  const loadProjects = async () => {
    try {
      const data = await getProjects(industryFilter !== 'all' ? industryFilter : null);
      setProjects(data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(p => 
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="projects-list-page">
        <PageTabs tabs={HUB_TABS} testIdPrefix="dmaic-tab" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">DMAIC Projects</h1>
            <p className="text-muted-foreground font-body mt-1">
              Manage your improvement projects using the DMAIC methodology
            </p>
          </div>
          <Button asChild data-testid="new-project-btn" className={isGuest ? 'hidden' : ''}>
            <Link to="/projects/new">
              <Plus className="h-4 w-4 mr-2" /> New Project
            </Link>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-projects"
            />
          </div>
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-industry">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Industries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industries.map((ind) => (
                <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div 
                key={project.project_id} 
                className={`block ${isGuest ? 'cursor-default' : ''}`}
                data-testid={`project-card-${project.project_id}`}
                onClick={() => { if (!isGuest) { window.location.href = `/projects/${project.project_id}`; } }}
              >
                <Card className="h-full card-hover">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <Badge className="capitalize">{project.industry}</Badge>
                      <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>
                        {project.status}
                      </Badge>
                    </div>
                    <CardTitle className="mt-3 line-clamp-1">{project.title}</CardTitle>
                    <CardDescription className="line-clamp-2">{project.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                          <span>Phase Progress</span>
                          <span>{Math.round(getPhaseProgress(project.phases))}%</span>
                        </div>
                        <Progress value={getPhaseProgress(project.phases)} className="h-2" />
                      </div>
                      <div className="flex gap-1">
                        {phases.map((phase) => (
                          <div
                            key={phase.key}
                            className={`h-1.5 flex-1 rounded-full ${
                              project.phases?.[phase.key]?.status === 'completed' 
                                ? phase.color 
                                : project.phases?.[phase.key]?.status === 'in_progress'
                                  ? `${phase.color} opacity-50`
                                  : 'bg-muted'
                            }`}
                            title={phase.label}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(project.created_at).toLocaleDateString()}
                        </span>
                        {project.team_members?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {project.team_members.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <Card className="py-16">
            <CardContent className="text-center">
              <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No projects found</h3>
              <p className="text-muted-foreground font-body mb-4">
                {emptyStateMsg(isGuest, search, 'Start your first DMAIC project')}
              </p>
              {!isGuest && (
                <Button asChild>
                  <Link to="/projects/new">
                    <Plus className="h-4 w-4 mr-2" /> Create Project
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProjectsListPage;
